import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brandmark } from "@/components/Brand";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const go = (u) => nav(u.role === "contractor" ? "/contractor" : ["staff", "admin"].includes(u.role) ? "/staff" : "/portal");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(path, form);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      go(data);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="login-grid" style={{ minHeight: "100vh", display: "grid" }}>
      <style>{`.login-grid{grid-template-columns:1fr}.login-art{display:none}@media(min-width:900px){.login-grid{grid-template-columns:1.1fr 1fr}.login-art{display:block!important}}`}</style>
      <div className="login-art" style={{ background: "var(--ink)", position: "relative", overflow: "hidden" }}>
        <img src="https://images.unsplash.com/photo-1633368098496-be7b5571547c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.55 }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(33,29,24,.3), rgba(33,29,24,.85))" }} />
        <div style={{ position: "absolute", bottom: 56, left: 52, right: 52, color: "var(--soft)" }}>
          <span className="lbl" style={{ color: "var(--sand)" }}>Solvana Group</span>
          <p className="font-serif-c" style={{ fontSize: 40, fontWeight: 300, lineHeight: 1.2, marginTop: 16 }}>
            Your home, handled, see everything, from anywhere.
          </p>
        </div>
      </div>

      <div style={{ display: "grid", placeItems: "center", padding: "40px 24px" }}>
        <div style={{ width: "100%", maxWidth: 380 }}>
          <Link to="/" style={{ display: "inline-block", marginBottom: 40 }}><Brandmark fontSize={24} tagline /></Link>
          <h1 style={{ fontSize: 40, marginBottom: 8 }}>{mode === "login" ? "Client login" : "Create account"}</h1>
          <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 30 }}>
            {mode === "login" ? "Access your property, reports and documents." : "Register to access your Solvana portal."}
          </p>

          <button data-testid="google-login-btn" onClick={google} className="btn-outline" style={{ width: "100%", marginBottom: 22, gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.1 0 24 0 14.6 0 6.4 5.4 2.6 13.2l7.8 6.1C12.2 13.6 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7C43.6 37.9 46.5 31.8 46.5 24.5z"/><path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6.1C1 16.5 0 20.1 0 24s1 7.5 2.6 10.8l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.4l-7.3-5.7c-2 1.4-4.6 2.2-7.7 2.2-6.4 0-11.8-4.1-13.6-9.8l-7.8 6.1C6.4 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </button>

          <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 11, letterSpacing: ".2em", margin: "6px 0 22px" }}>OR</div>

          <form onSubmit={submit}>
            {mode === "register" && (
              <input data-testid="name-input" className="inp" style={{ marginBottom: 12 }} placeholder="Full name" value={form.name} onChange={upd("name")} required />
            )}
            <input data-testid="email-input" className="inp" style={{ marginBottom: 12 }} type={mode === "register" ? "email" : "text"} placeholder={mode === "register" ? "Email" : "Email or username"} value={form.email} onChange={upd("email")} required />
            <div style={{ position: "relative", marginBottom: 20 }}>
              <input data-testid="password-input" className="inp" style={{ paddingRight: 44, marginBottom: 0, width: "100%" }} type={showPw ? "text" : "password"} placeholder="Password" value={form.password} onChange={upd("password")} required />
              <button type="button" data-testid="toggle-password-btn" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--muted)", display: "flex", padding: 0 }}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <button data-testid="submit-login-btn" type="submit" className="btn-ink" style={{ width: "100%" }} disabled={busy}>
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div style={{ marginTop: 22, fontSize: 13, color: "var(--muted)", textAlign: "center" }}>
            {mode === "login" ? (
              <>New owner? <button className="link-u" style={{ background: "none", border: "none" }} onClick={() => setMode("register")}>Create an account</button></>
            ) : (
              <>Already registered? <button className="link-u" style={{ background: "none", border: "none" }} onClick={() => setMode("login")}>Sign in</button></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
